import type { IPlaceRepository } from "@/core/repositories/interfaces/IPlaceRepository";
import type {
  CreatePlaceInput,
  PlaceEditor,
  PlaceEditorRow,
  PlaceLibraryTab,
  PlaceLibraryTabCounts,
  PlaceListPage,
  PlaceOpeningHours,
  PlaceSelectOption,
  PlaceTaxonomy,
  PlaceLibrarySort,
  PlaceLibrarySortState,
  UpdatePlaceContentInput,
  UpdatePlaceContentRepositoryResult,
  UpdatePlaceInternalNotesInput,
  UpdatePlaceCoverImageInput,
  UpdatePlaceStatusInput,
  UpdatePlaceTaxonomyInput,
} from "@/core/services/types/place.types";
import type { LibraryPageSize } from "@/core/services/types/library-pagination.types";
import {
  PLACE_CREATE_DEFAULT_NAME,
  PLACE_LIBRARY_TABS,
} from "@/core/services/types/place.types";

function castEditor(raw: PlaceEditorRow | null): PlaceEditor | null {
  if (!raw) return null;
  return {
    ...raw,
    opening_hours_structured: raw.opening_hours_structured as PlaceOpeningHours | null,
  };
}
import { createClient } from "@/lib/supabase";

const PLACE_ADMIN_LIST_SELECT =
  "id, name, city, zip_code, place_type, status, created_at, updated_at, cover_image_url, google_place_id, address" as const;

const PLACE_EDITOR_COLUMNS =
  "id, name, address, city, zip_code, department_name, website_url, ticketing_url, google_place_id, latitude, longitude, place_type, description_short, description_long, cover_image_url, status, updated_at, opening_hours_structured, internal_notes" as const;

function escapeIlikePattern(value: string): string {
  return value
    .replace(/\\/g, "\\\\")
    .replace(/"/g, '\\"')
    .replace(/%/g, "\\%")
    .replace(/_/g, "\\_");
}

function buildPlaceSearchFilter(search: string): string {
  const pattern = `%${escapeIlikePattern(search)}%`;
  return `name.ilike."${pattern}",description_short.ilike."${pattern}",city.ilike."${pattern}"`;
}

function applySearchFilter<T extends { or: (filters: string) => T }>(
  query: T,
  search: string,
): T {
  if (!search) return query;
  return query.or(buildPlaceSearchFilter(search));
}

function emptyLibraryTabCounts(): PlaceLibraryTabCounts {
  return {
    ALL: 0,
    DRAFT: 0,
    PENDING_REVIEW: 0,
    PUBLISHED: 0,
    ARCHIVED: 0,
  };
}

type OrderableQuery = {
  order: (
    column: string,
    options?: {
      ascending?: boolean;
      foreignTable?: string;
      nullsFirst?: boolean;
    },
  ) => OrderableQuery;
};

function applyPlaceLibrarySortColumn(
  query: OrderableQuery,
  sort: PlaceLibrarySort,
): OrderableQuery {
  const ascending = sort.direction === "asc";

  switch (sort.column) {
    case "name":
      return query.order("name", { ascending });
    case "place_type":
      return query.order("place_type", { ascending, nullsFirst: true });
    case "city":
      return query.order("city", { ascending, nullsFirst: true });
    case "zip_code":
      return query.order("zip_code", { ascending, nullsFirst: true });
    case "created_at":
      return query.order("created_at", { ascending });
    case "updated_at":
      return query.order("updated_at", { ascending });
    case "status":
      return query.order("status", { ascending });
  }
}

function applyPlaceLibraryOrder(
  query: OrderableQuery,
  sort: PlaceLibrarySortState,
): OrderableQuery {
  if (sort) {
    return applyPlaceLibrarySortColumn(query, sort);
  }

  return query.order("updated_at", { ascending: false });
}

type PlaceTaxonomyRow = {
  taxonomy_id: string;
  taxonomy: { category: string } | { category: string }[] | null;
};

function normalizeTaxonomyRelation(
  value: PlaceTaxonomyRow["taxonomy"],
): { category: string } | null {
  if (!value) return null;
  return Array.isArray(value) ? (value[0] ?? null) : value;
}

function getCurrentMonthUtcRange(): { start: string; end: string } {
  const now = new Date();
  const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
  const end = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1));

  return {
    start: start.toISOString(),
    end: end.toISOString(),
  };
}

export class PlaceRepository implements IPlaceRepository {
  async createAdmin(input: CreatePlaceInput): Promise<PlaceEditor | null> {
    const supabase = await createClient();
    const name = input.name?.trim() || PLACE_CREATE_DEFAULT_NAME;

    const { data, error } = await supabase
      .from("places")
      .insert({
        name,
        status: input.status ?? "DRAFT",
        source_type: "MANUAL",
        latitude: 0,
        longitude: 0,
        publish_on_web: false,
        publish_on_mobile: false,
      })
      .select(PLACE_EDITOR_COLUMNS)
      .single();

    if (error) {
      console.error("[PlaceRepository.createAdmin]", error.message);
      return null;
    }

    return castEditor(data);
  }

  async deleteAdmin(id: string): Promise<boolean> {
    const supabase = await createClient();

    const { error } = await supabase.from("places").delete().eq("id", id);

    if (error) {
      console.error("[PlaceRepository.deleteAdmin]", error.message);
      return false;
    }

    return true;
  }

  async findAdminById(id: string): Promise<PlaceEditor | null> {
    const supabase = await createClient();

    const { data, error } = await supabase
      .from("places")
      .select(PLACE_EDITOR_COLUMNS)
      .eq("id", id)
      .maybeSingle();

    if (error) {
      console.error("[PlaceRepository.findAdminById]", error.message);
      return null;
    }

    return castEditor(data);
  }

  async updateAdminContent(
    id: string,
    input: UpdatePlaceContentInput,
  ): Promise<UpdatePlaceContentRepositoryResult> {
    const supabase = await createClient();

    const { data, error } = await supabase
      .from("places")
      .update({
        name: input.name,
        address: input.address,
        city: input.city,
        zip_code: input.zip_code,
        department_name: input.department_name,
        website_url: input.website_url,
        ticketing_url: input.ticketing_url,
        google_place_id: input.google_place_id,
        latitude: input.latitude,
        longitude: input.longitude,
        place_type: input.place_type,
        description_short: input.description_short,
        description_long: input.description_long,
        updated_at: new Date().toISOString(),
      })
      .eq("id", id)
      .select(PLACE_EDITOR_COLUMNS)
      .maybeSingle();

    if (error) {
      console.error("[PlaceRepository.updateAdminContent]", error.message);
      if (
        error.code === "23505" ||
        error.message.includes("uq_places_google_place_id_not_null")
      ) {
        return { ok: false, error: "duplicate_google_place_id" };
      }
      return { ok: false, error: "unknown" };
    }

    const editor = castEditor(data);
    if (!editor) {
      return { ok: false, error: "unknown" };
    }

    return { ok: true, data: editor };
  }

  async updateAdminStatus(
    id: string,
    input: UpdatePlaceStatusInput,
  ): Promise<PlaceEditor | null> {
    const supabase = await createClient();

    const { data, error } = await supabase
      .from("places")
      .update({
        status: input.status,
        updated_at: new Date().toISOString(),
      })
      .eq("id", id)
      .select(PLACE_EDITOR_COLUMNS)
      .maybeSingle();

    if (error) {
      console.error("[PlaceRepository.updateAdminStatus]", error.message);
      return null;
    }

    return castEditor(data);
  }

  async updateAdminInternalNotes(
    id: string,
    input: UpdatePlaceInternalNotesInput,
  ): Promise<PlaceEditor | null> {
    const supabase = await createClient();

    const { data, error } = await supabase
      .from("places")
      .update({
        internal_notes: input.internalNotes,
        updated_at: new Date().toISOString(),
      })
      .eq("id", id)
      .select(PLACE_EDITOR_COLUMNS)
      .maybeSingle();

    if (error) {
      console.error("[PlaceRepository.updateAdminInternalNotes]", error.message);
      return null;
    }

    return castEditor(data);
  }

  async updateAdminCoverImage(
    id: string,
    input: UpdatePlaceCoverImageInput,
  ): Promise<PlaceEditor | null> {
    const supabase = await createClient();

    const { data, error } = await supabase
      .from("places")
      .update({
        cover_image_url: input.coverImageUrl,
        updated_at: new Date().toISOString(),
      })
      .eq("id", id)
      .select(PLACE_EDITOR_COLUMNS)
      .maybeSingle();

    if (error) {
      console.error("[PlaceRepository.updateAdminCoverImage]", error.message);
      return null;
    }

    return castEditor(data);
  }

  async findByLibraryTab(
    tab: PlaceLibraryTab,
    page: number,
    pageSize: LibraryPageSize,
    search = "",
    sort: PlaceLibrarySortState = null,
  ): Promise<PlaceListPage> {
    const supabase = await createClient();
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;

    let query = supabase
      .from("places")
      .select(PLACE_ADMIN_LIST_SELECT, { count: "exact" });

    query = applySearchFilter(query, search);

    if (tab !== "ALL") {
      query = query.eq("status", tab);
    }

    query = applyPlaceLibraryOrder(query, sort) as typeof query;

    const { data, error, count } = await query.range(from, to);

    if (error) {
      console.error("[PlaceRepository.findByLibraryTab]", error.message);
      return { items: [], total: 0, page, pageSize };
    }

    return {
      items: data ?? [],
      total: count ?? 0,
      page,
      pageSize,
    };
  }

  async countByLibraryTab(): Promise<PlaceLibraryTabCounts> {
    const supabase = await createClient();
    const counts = emptyLibraryTabCounts();

    await Promise.all([
      (async () => {
        const { count, error } = await supabase
          .from("places")
          .select("id", { count: "exact", head: true });

        if (error) {
          console.error("[PlaceRepository.countByLibraryTab] ALL", error.message);
          return;
        }

        counts.ALL = count ?? 0;
      })(),
      ...PLACE_LIBRARY_TABS.filter((tab) => tab !== "ALL").map(async (status) => {
        const { count, error } = await supabase
          .from("places")
          .select("id", { count: "exact", head: true })
          .eq("status", status);

        if (error) {
          console.error(
            `[PlaceRepository.countByLibraryTab] ${status}`,
            error.message,
          );
          return;
        }

        counts[status] = count ?? 0;
      }),
    ]);

    return counts;
  }

  async countPublishedThisMonth(): Promise<number> {
    const supabase = await createClient();
    const { start, end } = getCurrentMonthUtcRange();

    const { count, error } = await supabase
      .from("places")
      .select("id", { count: "exact", head: true })
      .eq("status", "PUBLISHED")
      .gte("updated_at", start)
      .lt("updated_at", end);

    if (error) {
      console.error("[PlaceRepository.countPublishedThisMonth]", error.message);
      return 0;
    }

    return count ?? 0;
  }

  async updateOpeningHours(
    id: string,
    openingHours: PlaceOpeningHours | null,
  ): Promise<PlaceEditor | null> {
    const supabase = await createClient();

    const { data, error } = await supabase
      .from("places")
      .update({
        opening_hours_structured: openingHours,
        updated_at: new Date().toISOString(),
      })
      .eq("id", id)
      .select(PLACE_EDITOR_COLUMNS)
      .maybeSingle();

    if (error) {
      console.error("[PlaceRepository.updateOpeningHours]", error.message);
      return null;
    }

    return castEditor(data);
  }

  async findAllForSelect(): Promise<PlaceSelectOption[]> {
    const supabase = await createClient();

    const { data, error } = await supabase
      .from("places")
      .select("id, name, city")
      .order("name", { ascending: true });

    if (error) {
      console.error("[PlaceRepository.findAllForSelect]", error.message);
      return [];
    }

    return data ?? [];
  }

  async findSelectOptionById(id: string): Promise<PlaceSelectOption | null> {
    const trimmed = id.trim();
    if (!trimmed) return null;

    const supabase = await createClient();

    const { data, error } = await supabase
      .from("places")
      .select("id, name, city")
      .eq("id", trimmed)
      .maybeSingle();

    if (error) {
      console.error("[PlaceRepository.findSelectOptionById]", error.message);
      return null;
    }

    return data;
  }

  async searchForSelect(
    query: string,
    limit: number,
  ): Promise<PlaceSelectOption[]> {
    const trimmed = query.trim();
    if (!trimmed) return [];

    const supabase = await createClient();

    let dbQuery = supabase
      .from("places")
      .select("id, name, city")
      .order("name", { ascending: true })
      .limit(limit);

    dbQuery = applySearchFilter(dbQuery, trimmed);

    const { data, error } = await dbQuery;

    if (error) {
      console.error("[PlaceRepository.searchForSelect]", error.message);
      return [];
    }

    return data ?? [];
  }

  async findTaxonomy(placeId: string): Promise<PlaceTaxonomy> {
    const supabase = await createClient();

    const { data, error } = await supabase
      .from("place_taxonomy")
      .select("taxonomy_id, taxonomy:taxonomy_id(category)")
      .eq("place_id", placeId);

    if (error) {
      console.error("[PlaceRepository.findTaxonomy]", error.message);
      return {
        artFormIds: [],
        artisticPeriodIds: [],
        culturalAreaIds: [],
        exhibitionTypeIds: [],
      };
    }

    const artFormIds: string[] = [];
    const artisticPeriodIds: string[] = [];
    const culturalAreaIds: string[] = [];
    const exhibitionTypeIds: string[] = [];

    for (const row of data ?? []) {
      const taxonomy = normalizeTaxonomyRelation(row.taxonomy);
      if (!taxonomy) continue;

      if (taxonomy.category === "ART_FORM") {
        artFormIds.push(row.taxonomy_id);
      } else if (taxonomy.category === "ARTISTIC_PERIOD") {
        artisticPeriodIds.push(row.taxonomy_id);
      } else if (taxonomy.category === "CULTURAL_AREA") {
        culturalAreaIds.push(row.taxonomy_id);
      } else if (taxonomy.category === "EXHIBITION_TYPE") {
        exhibitionTypeIds.push(row.taxonomy_id);
      }
    }

    return { artFormIds, artisticPeriodIds, culturalAreaIds, exhibitionTypeIds };
  }

  async replaceTaxonomy(
    placeId: string,
    input: UpdatePlaceTaxonomyInput,
  ): Promise<boolean> {
    const supabase = await createClient();

    const { error } = await supabase.rpc("replace_content_taxonomy", {
      p_entity_type: "place",
      p_entity_id: placeId,
      p_taxonomy_ids: [
        ...input.artFormIds,
        ...input.artisticPeriodIds,
        ...input.culturalAreaIds,
        ...input.exhibitionTypeIds,
      ],
    });

    if (error) {
      console.error("[PlaceRepository.replaceTaxonomy]", error.message);
      return false;
    }
    return true;
  }
}
